import { isUndefined } from 'lodash-es';

import { reduceClosestBins } from './store/reducers.js';
import { useSetState } from './store/state-setters.js';
import { useHistogramFilteredData } from './useHistogramFilteredData.js';
import { useHistogramTooltip } from './useHistogramTooltip.js';
import { useHistogramXScale } from './useHistogramXScale.js';
import { useHistogramYScales } from './useHistogramYScales.js';
import type { RelativeAbsolutePosition } from '../../core/types/pointer-events.js';
import {
  findClosestBinX,
  getClosestBins,
} from '../components/overlays/utils/find-closest-bins.js';

/**
 * Returns a function to calculate the closest bins at a given position to reflect it on the state,
 * which also returns the highlighted series id.
 */
export const useSetClosestBins = () => {
  const setState = useSetState();
  const { xScale } = useHistogramXScale();
  const yScales = useHistogramYScales();
  const { series, binsMap, seriesMap } = useHistogramFilteredData();
  const { variant } = useHistogramTooltip();

  return (position?: RelativeAbsolutePosition) => {
    if (!isUndefined(position)) {
      const mouseX = xScale.invert(position.relative.x);

      const closestBinsX = findClosestBinX(
        mouseX,
        series,
        yScales,
        xScale,
        seriesMap,
        binsMap,
      );

      const { closestBin, closestBinsGrouped, closestBinsByClosestOne } =
        getClosestBins(position.relative.y, closestBinsX);

      const closestBinsByVariant =
        variant === 'grouped'
          ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            closestBinsGrouped.find((innerArray) =>
              innerArray.some((item) => item.id === closestBin?.id),
            )!
          : closestBinsByClosestOne;

      setState((prev) =>
        reduceClosestBins(prev, {
          closestBin,
          closestBinsGrouped,
          closestBinsByVariant,
        }),
      );

      return closestBin?.id;
    }

    setState((prev) => reduceClosestBins(prev, undefined));

    return undefined;
  };
};
